import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  BrandTemplateKind,
  TEMPLATE_ROLES,
  type BrandWorkspaceTemplate,
  type TemplateRegion,
  type TemplateRoleMap,
} from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { useProxyImage } from '../../lib/useProxyImage';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import { AlertBanner } from '../ui/AlertBanner';

/** How a region is addressed in the saved map: its id when it has one, otherwise its position. */
function refOf(region: TemplateRegion): string {
  // `#3` addresses by ordinal for an export that carried no ids. Prefixed so the two namespaces can
  // never collide with a real id, and so a later reader can tell which kind of address it is.
  return region.ref || `#${region.ordinal}`;
}

/** What the dropdown row says. The sample text is what actually lets someone tell two headlines apart. */
function labelOf(region: TemplateRegion): string {
  const sample = region.sample?.trim();
  const name = region.ref || `Unnamed ${region.kind}`;
  return sample ? `${region.ordinal}. ${name} — “${truncate(sample, 40)}”` : `${region.ordinal}. ${name}`;
}

function truncate(text: string, max: number): string {
  return text.length <= max ? text : `${text.slice(0, max - 1)}…`;
}

/**
 * Tell Smorgas Social which element in a template plays which part.
 *
 * The left half is the template itself with every region OUTLINED AND NUMBERED; the right half is one
 * dropdown per role, each option carrying the same number. That pairing is the whole interaction — a
 * person matches "3" in the drawing to "3." in the list, so nothing has to be clickable inside the
 * artwork and the design needs no hit-testing, no transform maths and no coordinate mapping.
 *
 * The outlined version arrives as SVG MARKUP from the server and is rendered here through a blob URL
 * in an `<img>`. Markup rather than a picture because this runtime has no rasteriser; `<img>` rather
 * than inline `innerHTML` because a user-uploaded SVG in the page is script in our origin, and an
 * `<img>` cannot execute — the server sanitizes it too, but neither defence is trusted alone.
 */
export function TemplateMappingModal({
  template,
  onClose,
  onSaved,
}: {
  /** The template being mapped. Null keeps the dialog closed. */
  template: BrandWorkspaceTemplate | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [map, setMap] = useState<TemplateRoleMap>({});
  const [error, setError] = useState<string | null>(null);
  const isSvg = template?.kind === BrandTemplateKind.FIGMA_SVG;

  /** The outlined markup plus the region list, from one response so they describe one parse. */
  const annotated = useQuery({
    queryKey: ['brand-template-annotated', template?.id],
    queryFn: ({ signal }) => api.getBrandTemplateAnnotated(template!.id, signal),
    enabled: template != null && isSvg,
  });

  /** Regions for a kind we cannot outline — the detail read supplies them without the markup. */
  const detail = useQuery({
    queryKey: ['brand-template', template?.id],
    queryFn: ({ signal }) => api.getBrandTemplate(template!.id, signal),
    enabled: template != null && !isSvg,
  });

  const regions = (isSvg ? annotated.data?.regions : detail.data?.regions) ?? [];

  /**
   * Seed from what is already saved, so reopening shows the existing mapping rather than a blank form.
   *
   * Keyed on the map's CONTENT, not the object. react-query hands back a fresh object on every
   * refetch — a window-focus refetch mid-edit would otherwise reset the dropdowns to the saved state
   * and silently discard what the person had just chosen.
   */
  const savedMapKey = JSON.stringify(template?.roleMap ?? null);
  useEffect(() => {
    setMap(template?.roleMap ?? {});
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- savedMapKey stands in for roleMap
  }, [template?.id, savedMapKey]);

  /** The outlined SVG as a blob URL. Revoked on change, so switching templates leaks nothing. */
  const previewUrl = useObjectUrl(annotated.data?.svg ?? null, 'image/svg+xml');

  /**
   * A .pptx cannot be outlined — there is no markup to draw region numbers onto — but the card in
   * the templates list already has a RENDER of it, and showing that beats showing nothing. Behind
   * the image proxy because the preview route is auth-gated like every other blob here.
   */
  const pptxPreview = useProxyImage(isSvg ? null : (template?.previewUrl ?? null));

  const save = useMutation({
    mutationFn: () => api.updateBrandTemplate(template!.id, { roleMap: map }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (err) =>
      setError(err instanceof ApiRequestError ? err.message : 'Could not save the mapping.'),
  });

  const missingRequired = TEMPLATE_ROLES.filter((r) => r.required && !map[r.role]).map(
    (r) => r.label,
  );
  const loading = annotated.isLoading || detail.isLoading;

  return (
    <Modal
      open={template != null}
      onClose={onClose}
      size="5xl"
      title={template ? `Map “${template.nickname}”` : 'Map template'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={save.isPending}
            disabled={missingRequired.length > 0 || regions.length === 0}
            onClick={() => save.mutate()}
          >
            Save mapping
          </Button>
        </>
      }
    >
      <div className="grid gap-5 lg:grid-cols-[1.3fr,1fr]">
        {/* Left: the template, outlined and numbered. */}
        <div className="min-w-0">
          {loading ? (
            <div className="flex h-64 items-center justify-center rounded-xl bg-page text-sm text-ink-muted">
              Reading the template…
            </div>
          ) : isSvg && previewUrl ? (
            <img
              src={previewUrl}
              alt={`${template?.nickname} with each region outlined and numbered`}
              // max-h so a tall 1080×1350 story template still fits beside the dropdowns without
              // the dialog scrolling in two directions at once. bg-white on the image itself, so a
              // transparent template stays legible on a dark card; sized to the picture (w-auto,
              // centred) rather than the column, so the white never fills a tall template's letterbox.
              className="mx-auto block max-h-[60vh] w-auto max-w-full rounded-xl border border-line bg-white object-contain"
            />
          ) : pptxPreview ? (
            <div className="space-y-2">
              <img
                src={pptxPreview}
                alt={`${template?.nickname}, as this app reads it`}
                className="mx-auto block max-h-[60vh] w-auto max-w-full rounded-xl border border-line bg-white object-contain"
              />
              {/* Said plainly: the picture is OUR reading of the file, and the regions are not
                  numbered on it the way an SVG's are, because there is no markup to draw on. */}
              <p className="text-xs text-ink-muted">
                This is how Smorgas Social reads the file — regions aren’t outlined on it.
              </p>
            </div>
          ) : (
            <div className="rounded-xl border border-line bg-page p-4 text-sm text-ink-label">
              {template?.kind === BrandTemplateKind.CANVA_PPTX
                ? 'This .pptx hasn’t been rendered yet, so there is nothing to show here.'
                : 'This template couldn’t be drawn. You can still map its regions from the list.'}
            </div>
          )}
        </div>

        {/* Right: one dropdown per role. */}
        <div className="space-y-4">
          {regions.length === 0 && !loading ? (
            <AlertBanner tone="warning">
              No mappable regions were found in this template.
            </AlertBanner>
          ) : (
            TEMPLATE_ROLES.map((role) => {
              // The regions worth offering for this role. Text roles get text elements; image and
              // logo get the picture and shape slots — offering a <text> as the logo slot would let
              // someone build a mapping the generator can only ignore.
              const isTextRole = role.role === 'headline' || role.role === 'subhead';
              const choices = regions.filter((region) =>
                isTextRole ? region.kind === 'text' : region.kind !== 'text',
              );
              return (
                <div key={role.role} className="space-y-1.5">
                  {/* A div, not a <label>: Select renders a hidden native <select> BEFORE its
                      trigger, so a label without htmlFor binds to the wrong control. */}
                  <div className="text-sm font-medium text-ink-strong">
                    {role.label}
                    {role.required && <span className="ml-1 text-red-600 dark:text-error-dark">*</span>}
                  </div>
                  <Select
                    aria-label={role.label}
                    value={map[role.role] ?? ''}
                    placeholder={choices.length ? 'Not mapped' : 'Nothing suitable in this template'}
                    disabled={choices.length === 0}
                    onChange={(e) =>
                      setMap((prev) => {
                        const next = { ...prev };
                        // Delete rather than store '' — "unmapped" and "mapped to nothing" are
                        // different states, and the contract says an unmapped role is ABSENT.
                        if (e.target.value) next[role.role] = e.target.value;
                        else delete next[role.role];
                        return next;
                      })
                    }
                  >
                    <option value="">Not mapped</option>
                    {choices.map((region) => (
                      <option key={region.ordinal} value={refOf(region)}>
                        {labelOf(region)}
                      </option>
                    ))}
                  </Select>
                  {/* The dead end this closes: a layer that LOOKS like a heading but was converted
                      to outlines arrives as paths, so it is offered under Image and never under a
                      text role — and the disabled dropdown alone gives no way to work that out. */}
                  {choices.length === 0 && isTextRole && (
                    <p className="text-xs text-ink-muted">
                      No live text in this template. Text converted to outlines exports as paths,
                      which can be placed but not rewritten — re-export with the type left live to
                      map it here.
                    </p>
                  )}
                </div>
              );
            })
          )}

          {regions.length > 0 && !regions.some((r) => r.ref) && (
            <p className="text-xs text-ink-muted">
              This export carried no layer names, so regions are addressed by position. Re-exporting
              a changed template may shift them — name your Figma layers to make the mapping stick.
            </p>
          )}

          {missingRequired.length > 0 && regions.length > 0 && (
            <p className="text-xs text-ink-muted">
              {missingRequired.join(' and ')} {missingRequired.length === 1 ? 'is' : 'are'} required —
              a design with no headline has nothing to say.
            </p>
          )}
          {error && <p className="text-sm text-red-600 dark:text-error-dark">{error}</p>}
        </div>
      </div>
    </Modal>
  );
}

/**
 * A blob object URL over a string, revoked whenever the string changes or the component unmounts.
 *
 * Written as a hook rather than inline so the revoke cannot be forgotten: an object URL held by a
 * long-lived dialog pins its bytes in memory for the life of the document.
 */
function useObjectUrl(content: string | null, type: string): string | null {
  const blob = useMemo(() => (content ? new Blob([content], { type }) : null), [content, type]);
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return;
    }
    const created = URL.createObjectURL(blob);
    setUrl(created);
    return () => {
      URL.revokeObjectURL(created);
      setUrl(null);
    };
  }, [blob]);
  return url;
}
